import React from 'react';
import { DataBody } from '../../types';
import * as api from '../../services/api';
import FieldTooltip from '../shared/FieldTooltip';
import SourceSelect, { SourceSelection } from '../shared/SourceSelect';

/**
 * The ONE "Build New Data Body" form.
 *
 * There used to be two: the Dashboard's top-right modal (name + retention +
 * rate + concurrency) and the /new builder page (name only — it silently
 * skipped all three settings, so a body created there always got the server
 * defaults and the user had no idea). Both now render this component, so
 * there is exactly one definition of what creating a data body asks for.
 *
 * Fields, in order:
 *   Name        — required
 *   Source      — required. Who sends leads here. Combobox: pick an existing
 *                 source or type a new name, which gets created with the body
 *                 and lands in the Sources directory.
 *   Retention / Rate / Concurrency — same three settings as before.
 */

export interface BuildFormState {
  name: string;
  source: SourceSelection;
  retention: string;
  rateLimit: string;
  concurrency: string;
}

export function emptyBuildForm(): BuildFormState {
  return { name: '', source: null, retention: '30', rateLimit: '10', concurrency: '10' };
}

/**
 * Shared submit path. Throws with a user-facing message on validation
 * failure so both call sites surface identical errors.
 */
export async function createFromBuildForm(form: BuildFormState): Promise<DataBody> {
  const name = form.name.trim();
  if (!name) {throw new Error('Name is required');}
  if (!form.source) {throw new Error('Source is required — pick one or type a new name');}

  return api.createDataBody(name, {
    dataRetentionDays: parseInt(form.retention, 10),
    rateLimitPerSecond: parseInt(form.rateLimit, 10),
    concurrencyLimit: parseInt(form.concurrency, 10),
    ...(form.source.kind === 'existing'
      ? { sourceId: form.source.sourceId }
      : { sourceName: form.source.name }),
  });
}

interface Props {
  value: BuildFormState;
  onChange: (next: BuildFormState) => void;
  /** Fired on Enter in the Name field. */
  onSubmit?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export default function BuildDataBodyForm({ value, onChange, onSubmit, autoFocus, disabled }: Props) {
  const set = <K extends keyof BuildFormState>(key: K, v: BuildFormState[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <>
      <div className="form-group">
        <label className="form-label">Name</label>
        <input
          className="form-input"
          type="text"
          placeholder="e.g., Facebook Leads Pipeline"
          value={value.name}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={e => set('name', e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && onSubmit) {onSubmit();} }}
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          Source
          <FieldTooltip text="The company, partner or person responsible for sending leads into this data body — whoever will be POSTing to its webhook URL. Pick an existing source or type a new name to create one. A source can own several data bodies, and Reporting rolls all of them up under it." />
        </label>
        <SourceSelect
          value={value.source}
          onChange={v => set('source', v)}
          disabled={disabled}
        />
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">
            Data Retention
            <FieldTooltip text="How long leads stay accessible in this data body. After this period, leads are removed. If retention is over 90 days, per-lead processing details are archived to cold storage at the 90-day mark. Choosing 'Forever' keeps leads indefinitely (cold-storage access fees may apply)." />
          </label>
          <select
            className="form-select"
            value={value.retention}
            disabled={disabled}
            onChange={e => set('retention', e.target.value)}
          >
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">365 days</option>
            <option value="0">Forever</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">
            Rate Limit (per second)
            <FieldTooltip text="Maximum number of leads this data body will accept per second. Sources sending faster get a 429 response telling them to slow down." />
          </label>
          <input
            className="form-input"
            type="number"
            value={value.rateLimit}
            disabled={disabled}
            onChange={e => set('rateLimit', e.target.value)}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">
            Concurrency Limit
            <FieldTooltip text="Maximum number of leads being processed at the same time. Catches misbehaving sources that open many parallel connections to bypass the rate limit." align="right" />
          </label>
          <input
            className="form-input"
            type="number"
            value={value.concurrency}
            disabled={disabled}
            onChange={e => set('concurrency', e.target.value)}
          />
        </div>
      </div>
    </>
  );
}
